import React, { createContext, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useUser } from './UserContext';
import UserLoginPopUp from '../Abstracts/Modals/UserLoginPopUp/UserLoginPopUp';
import ListingRequestModal from '../Abstracts/ListingRequest/ListingRequestModal/ListingRequestModal';
import { ListingRequestSource } from '../../Interface/ListingRequest';
import { useListingRequestEnabled } from '../Hooks/useListingRequestEnabled';

interface IListingRequestContext {
  openListingRequest: (source: ListingRequestSource) => void;
}

const ListingRequestContext = createContext<IListingRequestContext>({
  openListingRequest: () => undefined,
});

/**
 * The CTA sits in the search dropdown, in every product list and in the launch banner, so the
 * form is mounted once here instead of three times. Sending a request requires a login, which is
 * why an anonymous click opens the login popup first and the form afterwards.
 */
export function ListingRequestProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { user } = useUser();
  const listingRequestEnabled = useListingRequestEnabled();
  const location = useLocation();
  const [source, setSource] = useState<ListingRequestSource | null>(null);
  const [showLogin, setShowLogin] = useState<boolean>(false);

  // The form is rendered above the routes, so a navigation would leave it hanging over the new page.
  // Keyed on location.key, because a suggested product can be on the current path with a new query.
  useEffect(() => {
    setSource(null);
    setShowLogin(false);
  }, [location.key]);

  const openListingRequest = (nextSource: ListingRequestSource) => {
    setSource(nextSource);
    if (!user) {
      setShowLogin(true);
    }
  };

  const onLoginPopupToggle = (show: boolean) => {
    setShowLogin(show);
    if (!show && !user) {
      // Login was dismissed - drop the pending source instead of opening the form later on.
      setSource(null);
    }
  };

  return (
    <ListingRequestContext.Provider value={{ openListingRequest }}>
      {children}
      {listingRequestEnabled && (
        <>
          <UserLoginPopUp
            showLoginPopup={showLogin}
            setShowLoginPopup={onLoginPopupToggle}
            onLoginSuccess={() => setShowLogin(false)}
          />
          <ListingRequestModal
            show={!showLogin && source !== null && !!user}
            source={source ?? 'search'}
            onHide={() => setSource(null)}
          />
        </>
      )}
    </ListingRequestContext.Provider>
  );
}

export function useListingRequest() {
  const context = useContext(ListingRequestContext);
  if (!context) {
    throw new Error(
      'useListingRequest must be used within ListingRequestProvider'
    );
  }
  return context;
}
